import React, { FunctionComponent, JSX } from 'react';

import pluralize from 'pluralize';

import {
	BarDatum,
	EvBodyDefault,
	EVINCED_SEVERITIES,
	EVINCED_SEVERITIES_IDS,
	EvincedColors,
	EvRaceBar
} from '@evinced-private/ui-common';

import { calcDataForComponentsRaceGraph, getTopTenComponents } from '../../../helpers/ChartsHelper';
import { CustomSeverityNames, resolveSeverityLabel } from '../../../helpers/SeverityLabelHelper';
import { EvChartTableLink } from '../../common/ev-chart-table-link/EvChartTableLink';

import './CriticalComponentsRaceBar.scss';

interface IEvincedAdvantageBarProps {
	componentsWithCritical: BarDatum[];
	totalCriticalIssues: number;
	propertyId: string;
	scanId: string;
	className?: string;
	maxHeight?: number;
	customSeverityNames?: CustomSeverityNames;
	onTableLinkClick?: (componentId: string) => void;
}

const CriticalComponentsRaceBarV3: FunctionComponent<IEvincedAdvantageBarProps> = ({
	componentsWithCritical,
	totalCriticalIssues,
	className,
	propertyId,
	scanId,
	maxHeight = 320,
	customSeverityNames = {},
	onTableLinkClick
}) => {
	const topTenComponents = getTopTenComponents(componentsWithCritical);
	const { barData, percentCovered } = calcDataForComponentsRaceGraph(
		topTenComponents,
		totalCriticalIssues,
		propertyId,
		scanId
	);

	const criticalLabelLower = resolveSeverityLabel(
		EVINCED_SEVERITIES.CRITICAL,
		customSeverityNames
	).toLowerCase();

	const fieldFormatter = (componentId: string): React.ReactNode => {
		return <EvChartTableLink onClick={() => onTableLinkClick(componentId)} value={componentId} />;
	};

	const title = 'Breakdown by components';

	const isError = barData.length === 0;

	const renderErrorState = (): JSX.Element => {
		return <EvBodyDefault as="p">{`No grouped ${criticalLabelLower} issues found.`}</EvBodyDefault>;
	};

	return (
		<EvRaceBar
			title={title}
			data={barData}
			description={
				isError
					? ''
					: `Resolve ${percentCovered}% of ${criticalLabelLower} issues - by fixing ${barData.length} ${pluralize('component', barData.length)}`
			}
			itemTypeLabel="Component"
			itemTooltipPercentDescription={`of ${criticalLabelLower} issues`}
			className={className}
			color={EvincedColors.newSeverityColors[EVINCED_SEVERITIES_IDS.CRITICAL]}
			maxHeight={maxHeight}
			headerTooltipProps={{
				tooltipText: `An overview of the top components needing fixes, based on the total number of ${criticalLabelLower} issues. Resolving an issue within a component group may potentially resolve the entire group.`,
				tooltipTitle: `What is ${criticalLabelLower} issues breakdown by components`,
				tooltipPlacement: 'right'
			}}
			tableProps={{
				columnsPattern: [
					{ name: 'Component', field: 'id', formatter: fieldFormatter },
					{ name: 'Issues Percentage', field: 'percent' }
				]
			}}
			showLabels={false}
			isError={isError}
			renderErrorState={isError && renderErrorState}
			disabledSwitcher={isError}
		/>
	);
};

export { CriticalComponentsRaceBarV3 };
